import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  CircularProgress,
  Tooltip,
  Chip,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { hosturl } from '../libs/Constant';
import { motion } from 'framer-motion';

const ReceiveFromManagement = () => {
  const theme = useTheme();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState({ id: '', name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const token = localStorage.getItem("token");

  // Custom colors for the UI
  const colors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    background: '#f8f9fa',
    cardBackground: '#ffffff',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568'
  };

  // Fetch all entries
  const fetchEntries = async () => {
    try {
      const response = await fetch(`${hosturl}/receive-from`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch entries');
      }
      
      const data = await response.json();
      setEntries(data.result || []);
    } catch (err) {
      setError(err.message);
      showSnackbar(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Open dialog for adding new entry
  const handleOpenDialog = () => {
    setCurrentEntry({ id: '', name: '' });
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentEntry({ id: '', name: '' });
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (entry) => {
    setCurrentEntry(entry);
    setOpenDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentEntry({ id: '', name: '' });
  };

  // Submit form (create entry)
  const handleSubmit = async () => {
    try {
      const response = await fetch(`${hosturl}/receive-from`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: currentEntry.name }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create entry');
      }
      
      showSnackbar('Receive created successfully');
      fetchEntries();
      handleCloseDialog();
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  // Delete entry
  const handleDelete = async () => {
    try {
      const response = await fetch(`${hosturl}/receive-from/${currentEntry._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }
      
      showSnackbar('Receive deleted successfully');
      fetchEntries();
      handleCloseDeleteDialog();
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  return (
    <Box sx={{ 
      p: 3,
      background: colors.background,
      minHeight: '100vh'
    }}>
      {/* Header Receive */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        p: 3,
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
        borderRadius: 2,
        boxShadow: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CategoryIcon sx={{ fontSize: 40, mr: 2 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Receive Management
            </Typography>
            <Typography variant="subtitle1">
              Manage all Receives in the system
            </Typography>
          </Box>
        </Box>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              color: colors.primary,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1
            }}
          >
            Add New Receive
          </Button>
        </motion.div>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 3,
        borderRadius: 3,
        background: colors.cardBackground,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '300px'
          }}>
            <CircularProgress size={60} thickness={4} sx={{ color: colors.primary }} />
          </Box>
        ) : error ? (
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: theme.palette.error.light,
            borderRadius: 2
          }}>
            <Typography color="error" variant="h6">
              {error}
            </Typography>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={fetchEntries}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Paper>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                }}>
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}>
                    Receive from 
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '1rem',
                    width: '120px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        color: colors.textSecondary
                      }}>
                        <CategoryIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6">
                          No Receives found
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Click "Add New Receive" to create one
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow 
                      key={entry._id} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { 
                          backgroundColor: 'rgba(0, 0, 0, 0.02)' 
                        },
                        '&:last-child td': {
                          borderBottom: 0
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        fontSize: '1rem',
                        color: colors.textPrimary
                      }}>
                        {entry.name}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete Receive" arrow>
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDeleteDialog(entry)}
                            sx={{
                              '&:hover': {
                                background: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add Receive Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          color: 'white',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add New Receive
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Receive Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentEntry.name}
            onChange={(e) => setCurrentEntry({ ...currentEntry, name: e.target.value })}
            sx={{ mt: 2 }}
            InputProps={{
              sx: {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: '#e2e8f0'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#e2e8f0',
              color: colors.textSecondary
            }}
          >
            Cancel
          </Button>
          <motion.div whileHover={{ scale: 1.03 }}>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              startIcon={<CheckIcon />}
              disabled={!currentEntry.name.trim()}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                '&:disabled': {
                  background: '#e2e8f0'
                }
              }}
            >
              Create Receive
            </Button>
          </motion.div>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog} 
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: theme.palette.error.main,
          color: 'white',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Confirm Delete
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDeleteDialog}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this Receive?
          </Typography>
          <Paper elevation={0} sx={{ 
            p: 2,
            background: '#fff8f8',
            borderRadius: 2,
            borderLeft: `4px solid ${theme.palette.error.main}`
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {currentEntry.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              This action cannot be undone
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: '#e2e8f0',
              color: colors.textSecondary
            }}
          >
            Cancel
          </Button>
          <motion.div whileHover={{ scale: 1.03 }}>
            <Button 
              onClick={handleDelete} 
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: 'none'
              }}
            >
              Delete Receive
            </Button>
          </motion.div>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Paper elevation={3} sx={{
          p: 2,
          borderRadius: 2,
          background: snackbar.severity === 'error' ? 
            theme.palette.error.main : 
            theme.palette.success.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          {snackbar.severity === 'error' ? (
            <DeleteIcon sx={{ mr: 1 }} />
          ) : (
            <CheckIcon sx={{ mr: 1 }} />
          )}
          <Typography variant="body1">
            {snackbar.message}
          </Typography>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseSnackbar}
            sx={{ ml: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Snackbar>
    </Box>
  );
};

export default ReceiveFromManagement;